<template>
  <Intersect
    root-margin="150px 0px"
    @enter.once="enteringView"
  >
    <div
      class="discussion-container"
      :class="{ 'mt-4 mt-lg-0': forDiscussionPage }"
    >
      <div
        v-if="!forDiscussionPage"
        class="row"
      >
        <div
          v-if="showLoginMessage"
          class="col-12 col-md-6"
        >
          <div
            class="sign-in-banner"
            v-html="getLoginMessageHtml"
          />
        </div>
      </div>

      <PDPDiscussion
        :comment-link="commentLink"
        :is-loading-comments="isLoadingComments"
        :deal-name="deal.public_name"
        :deal-partner-user-ids="deal.partner_user_ids"
        :comments="comments"
        :filter-comments="filterComments"
        :force-show-all="forceShowAll"
        :show-all-children="showAllChildren"
        :user="user"
        :comment-type-title="commentTypeTitle"
        :comment-type-title-lc="commentTypeTitleLc"
        :has-items="hasItems"
        :for-questions="forQuestions"
        :for-reviews="forReviews"
        :is-moderator-view="isModeratorView"
        @on-click-refresh="onClickRefresh"
        @on-change-filter="onChangeFilter"
        @click-flag="onClickFlag"
        @reply-comment="replyComment"
        @toggle-comment="toggleCreateItemModal"
        @toggle-show-all-children="toggleShowAllChildren"
        @update-action="updateAction"
      />

      <div
        v-if="showLoadMoreCommentsButton"
        class="text-center mt-5 mb-3"
      >
        <!--
          jw: we are going to render this link as a button, and include a URL allowing us to
              properly handle loading deeper pages for search engine spiders.
        -->
        <a
          class="btn show-more-comments"
          :class="{ 'disabled': isFetching }"
          :href="showMoreButtonUrl"
          @click.prevent="fetchMoreComments()"
          @keydown.enter.prevent="fetchMoreComments()"
        >
          {{ showMoreButtonText }}
        </a>
      </div>

      <PDPCommentModal
        v-if="showCreateCommentModal"
        :type="showCreateReplyModal ? 'reply' : commentType"
        :parent-comment="replyParentComment"
        @click-close="toggleCreateItemModal"
        @on-submit="onSubmitComment"
      />

      <PDPQuestionModal
        v-if="showCreateQuestionModal"
        type="question"
        :deal="deal"
        :user="user"
        @click-close="toggleCreateItemModal"
        @update-questions="fetchComments(0)"
      />
    </div>
  </Intersect>
</template>

<script>
import throttle from 'lodash/throttle';
import catchify from 'catchify';
import get from 'lodash/get';
import { MAX_COMMENT_PAGE_SIZE } from '@/constants/global';
import Intersect from 'vue-intersect';
import PDPDiscussion from './PDPDiscussion.vue';
import { DiscussionType } from '../../../constants/discussions';

// defining this as a true constant outside of the component so that it is only built once.
const filterDict = {
  newest: '&sort=date&order=desc',
  oldest: '&sort=date&order=asc',
  latest: '&sort=date&order=desc', // Add filter to find children?
  helpful: '&sort=helpful',
  unapproved: '&moderator_filter=unapproved',
  unresolved: '&moderator_filter=unresolved',
  'rating-asc': '&sort=rating&order=asc',
  'rating-desc': '&sort=rating&order=desc',
  'plus-members': '&moderator_filter=plus-members',
  all: '',
};

export default {
  name: 'PDPDiscussionContent',
  components: {
    Intersect,
    PDPDiscussion,
    PDPCommentModal: () => import('../comment-modal/PDPCommentModal.vue'),
    PDPQuestionModal: () => import('../question/PDPQuestionModal.vue'),
  },
  inject: {
    forDiscussionPage: {
      default: false,
    },
  },
  props: {
    deal: {
      type: Object,
      required: true,
    },
    filterComments: {
      type: Boolean,
    },
    user: {
      type: Object,
      required: true,
    },
    commentType: {
      type: String,
      default: DiscussionType.review,
    },
    discussion: {
      type: Object,
      default: null,
    },
  },
  data() {
    // many of the settings change defaults based on whether we are rendering for the discussion
    // ~ page or not, so let's set proper defaults
    let enteredView = false;
    const comments = [];

    // now we can update the values established above if we know we are rendering for the
    // ~ discussion page
    if (this.forDiscussionPage) {
      comments.push(this.discussion);
      enteredView = true;
    }

    return {
      enteredView,
      comments,
      isLoadingComments: !this.forDiscussionPage,
      isFirstTimeRequestingComments: true,
      commentLink: {},
      filterQuery: '',
      isChildComment: false,
      replyId: null,
      replyLevel: 0,
      replyIndices: [],
      showCreateReplyModal: false,
      showCreateItemModal: false,
      sortQuery: '',
      // used to determine if we should include "Load More"
      lastFetchCount: 0,
      nextPageToFetch: 1,
      // we want to track whether to show all replies and maintain it as filters/sorts change
      showAllChildren: true,
      hasFetchedMoreManually: false,
      allItemsLoaded: false,
      replyParentComment: null,
    };
  },
  // Fetch is called on server-side when rendering the route,
  // ~ and on client-side when navigating. No need to call it manually
  async fetch() {
    const { isModerator } = this.user || {};

    // always load the comment statuses for moderators since this is only done once.
    if (isModerator) {
      this.fetchCommentStatuses();
    }

    // never fetch for the discussion page. We are only rendering for a single discussion and
    // ~ should never need to load more/different items.
    if (this.forDiscussionPage) {
      return;
    }

    // if the component was updated to "enteredView" prior to fetch then load the results.
    if (!this.enteredView) {
      return;
    }

    // We fetch directly if SSR, and for CSR if User is Moderator
    if (typeof window === 'undefined' || isModerator) {
      await this.fetchComments(this.initialPage);
    } else {
      this.throttle(); // Throttle for regular users
    }
  },
  computed: {
    userIsModerator() {
      return this.$store.getters['discussion/userIsModerator'];
    },
    userHasPurchased() {
      return this.$store.getters['discussion/userHasPurchased'];
    },
    searchTerm() {
      return this.$store.getters['discussion/searchTerm'];
    },
    linkHash() {
      return this.$route?.hash || '';
    },
    // Parse comment hash, LinkHash[0] => #
    linkHashIsValid() {
      if (!this.linkHash) {
        return false;
      }

      if (this.linkHash.length < 2 || ['#reviews', '#questions'].includes(this.linkHash)) {
        return false;
      }

      return ['q', 'r'].includes(this.linkHash[1]);
    },
    discussionPageLinkHashIsValid() {
      if (!this.linkHash || this.linkHash.length < 2) {
        return false;
      }

      return !!parseInt(this.linkHash.slice(1), 10); // true if linkHash[1:] is integer
    },
    isModeratorView() {
      return this.user.isAuthenticated && (this.userIsModerator || this.user.isStaff);
    },
    forQuestions() {
      return this.commentType === DiscussionType.question;
    },
    forReviews() {
      return this.commentType === DiscussionType.review;
    },
    commentTypeTitle() {
      return this.forQuestions ? 'Question' : 'Review';
    },
    commentTypeTitleLc() {
      return this.commentTypeTitle.toLowerCase();
    },
    getLoginMessageHtml() {
      const askOrLeave = this.forQuestions ? 'ask' : 'leave';
      const loginOrVerifyLink = !this.user.isAuthenticated ? '/accounts/login/' : '/account/email/';
      const loginOrVerifyText = !this.user.isAuthenticated ? 'log in' : 'verify your email';

      return `Please <a href="${loginOrVerifyLink}">${loginOrVerifyText}</a> to
              ${askOrLeave} a ${this.commentTypeTitleLc}.`;
    },
    showLoginMessage() {
      return !this.user.isAuthenticated || (!this.user.isEmailVerified && !this.user.isStaff);
    },
    showCreateCommentModal() {
      if (this.showCreateReplyModal) {
        return true;
      }

      return this.forReviews && this.showCreateItemModal;
    },
    showCreateQuestionModal() {
      return this.forQuestions && this.showCreateItemModal;
    },
    commentCount() {
      if (this.forQuestions) {
        return get(this, ['deal', 'deal_comment', 'comment_count'], 0);
      }

      return get(this, ['deal', 'deal_review', 'review_count'], 0);
    },
    forceShowAll() {
      return this.showAllChildren || this.searchTerm || this.threadFilterIsApplied;
    },
    threadFilterIsApplied() {
      return !!this.commentLink?.id;
    },
    searchOrThreadFilterIsApplied() {
      return !!this.searchTerm || this.threadFilterIsApplied;
    },
    showLoadMoreCommentsButton() {
      // never include the load more button on the discussion page. All replies are included on the
      // ~ the page, and we never want to show more than one discussion. So not needed.
      if (this.forDiscussionPage) {
        return false;
      }

      // if we have not entered view yet then we always want to include the load more button
      // ~ so that search engines can fetch discussions.
      if (!this.enteredView) {
        return true;
      }

      if (this.isLoadingComments || this.searchOrThreadFilterIsApplied) {
        return false;
      }

      return this.lastFetchCount >= MAX_COMMENT_PAGE_SIZE && !this.allItemsLoaded;
    },
    hasItems() {
      return !!this.comments?.length;
    },
    showMoreButtonUrl() {
      // we need to include a way to detect if we are rendering for this components, so lets
      // ~ include the short comment type and the next page.
      return `${this.deal.get_absolute_url}?pt=${this.shortCommentType}&p=${this.nextPageToFetch}`;
    },
    showMoreButtonText() {
      if (!this.enteredView) {
        return `Load ${this.commentTypeTitle}s`;
      }

      return this.hasFetchedMoreManually
        ? `Show all ${this.commentTypeTitle}s`
        : `Show more ${this.commentTypeTitle}s`;
    },
    isFetching() {
      return this.$store.getters['discussion/isFetching'];
    },
  },
  watch: {
    searchTerm() {
      this.fetchComments();
    },
  },
  async created() {
    this.shortCommentType = this.forQuestions ? 'q' : 'r';
    this.initialPage = 1;

    // URL fragments (hash) are only available on client-side so once
    // the component is created we need to force it to fetch the comments
    if (this.linkHashIsValid) {
      this.commentLink = {
        type: this.commentType,
        id: this.linkHash.slice(2),
      };

      // we need to flag this as true to allow fetch to actually get results.
      this.enteredView = true;
    }

    // let's try to handle loading a deeper page for search engine spiders.
    if (this.$route?.query?.pt === this.shortCommentType) {
      const routePage = parseInt(this.$route?.query?.p, 10);
      if (routePage) {
        this.initialPage = routePage;
        // we need to flag this as true to allow fetch to actually get results.
        this.enteredView = true;
      }
    }

    // force fetch immediately so we can scroll if hash has been found
    if (!this.forDiscussionPage && this.enteredView) {
      await this.fetchComments(this.initialPage);
    }
  },
  mounted() {
    if (this.forDiscussionPage && this.discussionPageLinkHashIsValid) {
      const linkedComment = document.getElementById(this.$route.hash.slice(1));

      if (linkedComment) {
        setTimeout(() => {
          // hack to allow smooth scroll to comment when navigating to discussion page from pdp
          window.scrollTo(0, 0);

          const top = window.scrollY + linkedComment.getBoundingClientRect().top;

          // taken from PDPScrollToElementMixin -- notes on why we do this way can be found there
          if ('scrollBehavior' in document.documentElement.style) {
            window.scrollTo({ top, behavior: 'smooth' });
          } else {
            window.scrollTo(0, top);
          }
        }, 1);
      }
    }
  },
  methods: {
    replyComment(data = {}) {
      const { comment, positionIndices } = data;

      if (!comment?.id) {
        this.$toastr.e('Something went wrong');
        return;
      }

      this.replyParentComment = comment;

      this.replyId = comment.id;
      this.replyIndices = positionIndices;
      this.replyLevel = (comment?.level || 0) + 1;

      this.toggleReplyModal();
    },
    onClickFlag() {
      // TODO -- Setup endpoint to flag
    },
    async onSubmitComment(data = {}) {
      let securityHash = this.deal.discussion.hash_question;
      let objectKey = this.deal.discussion.object_pk_question;

      if (this.forReviews) {
        securityHash = this.deal.discussion.hash_review;
        objectKey = this.deal.discussion.object_pk_review;
      }

      const {
        comment,
        notifyEmail,
        rating,
        title,
      } = data;

      const dataProps = {
        comment,
        content_type: `deals.${this.commentType}`,
        email: this.user.email,
        followup: notifyEmail,
        honeypot: '',
        name: this.user.username,
        object_pk: objectKey,
        rating: rating || 0,
        reply_to: this.replyId || 0,
        security_hash: securityHash,
        timestamp: this.deal.discussion.timestamp,
        title: title || '',
      };

      const dataKeys = Object.keys(dataProps);
      let formBody = [];

      for (let i = 0; i < dataKeys.length; i += 1) {
        const key = dataKeys[i];
        const encodedKey = encodeURIComponent(key);
        const encodedValue = encodeURIComponent(dataProps[key]);
        formBody.push(`${encodedKey}=${encodedValue}`);
      }

      formBody = formBody.join('&');

      // Matches django version headers
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-CSRFToken': this.$cookies.get('csrftoken'),
        'Accept-Encoding': 'gzip, deflate',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: '*/*',
      };

      await this.$axios.post(
        '/comments/api/discussion/',
        formBody,
        { headers },
      )
        .then((response) => {
          if (response && response.data && response.data.new_comment_id) {
            const {
              comment: respComment,
              new_comment_id: newId,
              rating: respRating,
              reply_to: replyId,
              timestamp,
              title: respTitle,
            } = response.data;

            const {
              avatar,
              dateJoined,
              dealsPurchased,
              hasBriefcase,
              hasPlus,
              id: userId,
              isEmailVerified,
              isModerator,
              username,
            } = this.user;

            let contentId = 84;
            let commentPrefix = 'q';

            if (this.forReviews) {
              contentId = 85;
              commentPrefix = 'c';
              this.hasReviewed = true;
            }

            const newComment = {
              allow_reply: false,
              approved: undefined,
              children: [],
              comment: respComment,
              id: newId,
              is_flagged: 0,
              is_removed: 0,
              level: this.replyLevel || 0,
              num_likes: 0,
              parent_id: replyId,
              permalink: `/comments/cr/${contentId}/${this.deal.discussion.object_pk}/#${commentPrefix}${newId}`,
              pinned: null,
              purchased: this.userHasPurchased,
              rating: respRating,
              resolved: this.forReviews, // Reviews are auto-resolved
              submit_date: timestamp,
              title: respTitle,
              user: {
                avatar,
                comment_blacklist: false,
                comment_whitelist: false,
                date_joined: dateJoined,
                deals_purchased: dealsPurchased,
                email_verified: isEmailVerified,
                has_briefcase: hasBriefcase,
                has_plus: hasPlus,
                id: userId,
                username,
              },
              user_moderator: isModerator,
              user_url: '', // This needed?
            };

            const updatedComments = [...this.comments];

            // Add comment to correct position in tree
            // 1st index is child, 2nd index is grandchild
            if (this.replyIndices.length === 1) {
              const { children } = updatedComments[this.replyIndices[0]];

              if (!children) {
                updatedComments[this.replyIndices[0]].children = [];
              }

              updatedComments[this.replyIndices[0]].children.unshift(newComment);
            } else if (this.replyIndices.length === 2) {
              const {
                children,
              } = updatedComments[this.replyIndices[0]].children[this.replyIndices[1]];

              if (!children) {
                updatedComments[this.replyIndices[0]].children[this.replyIndices[1]].children = [];
              }

              updatedComments[this.replyIndices[0]].children[this.replyIndices[1]].children
                .unshift(newComment);
            } else {
              updatedComments.unshift(newComment);
            }

            this.comments = updatedComments;

            this.$toastr.s('Success');
          }
        })
        .catch(() => {
          this.$toastr.e('Something went wrong');
        });

      if (this.replyId) {
        this.replyId = null;
        this.replyIndices = [];
        this.replyLevel = 0;
      }

      this.toggleCreateItemModal();
    },
    toggleCreateItemModal() {
      if (this.showCreateReplyModal) {
        this.toggleReplyModal();
        return;
      }

      this.replyParentComment = null;
      this.showCreateItemModal = !this.showCreateItemModal;
      this.updateScrollToTopVisibility();
    },
    toggleReplyModal() {
      this.showCreateReplyModal = !this.showCreateReplyModal;
      this.updateScrollToTopVisibility();
    },
    toggleShowAllChildren() {
      this.showAllChildren = !this.showAllChildren;
    },
    updateAction(data = {}) {
      const {
        commentUserId,
        id,
        type,
        value,
        done,
      } = data;

      this.setIsFetching(true);

      switch (type) {
        case 'approved': {
          let action = '1';

          if (typeof value === 'undefined' || value === null) {
            action = 'null';
          } else if (!value) {
            action = '0';
          }

          this.$axios.get(`/comments/approve_comment_ajx/?comment_id=${id}&approve_action=${action}`)
            .then((response) => {
              const { success } = (response && response.data) || {};

              done({ type, value, error: !success });
            })
            .catch(() => {
              done({ type, value, error: true });
            })
            .finally(() => {
              this.setIsFetching(false);
            });

          return;
        }
        case 'resolved': {
          const reqData = { xtdcomment_ptr_id: id, resolved: value };
          const headers = {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$cookies.get('csrftoken'),
          };

          this.$axios.put(
            `/discussions/api/resolved/${id}/`,
            reqData,
            { headers },
          )
            .then((response) => {
              const { xtdcomment_ptr_id: cId, resolved } = (response && response.data) || {};

              done({ type, value: resolved, error: cId < 0 });
            })
            .catch(() => {
              done({ type, value, error: true });
            })
            .finally(() => {
              this.setIsFetching(false);
            });

          return;
        }

        case 'pinned': {
          // todo:LED-1136: We need to support pinning for questions eventually.
          if (this.forQuestions) {
            return;
          }

          const action = value ? 1 : 0;

          this.$axios.get(`/comments/pin_comment_ajx/?comment_id=${id}&pin_action=${action}`)
            .then((response) => {
              const { success } = (response && response.data) || {};

              done({ type, value, error: !success });
            })
            .catch(() => {
              done({ type, value, error: true });
            })
            .finally(() => {
              this.setIsFetching(false);
            });

          return;
        }

        case 'blacklist':
        case 'whitelist': {
          const action = value ? 1 : 0;

          this.$axios.get(`/comments/${type}_author_ajx/?user_id=${commentUserId}&${type}_action=${action}`)
            .then((response) => {
              const { success } = (response && response.data) || {};
              const error = !success;

              done({ type, value, error });
            })
            .catch(() => {
              done({ type, value, error: true });
            })
            .finally(() => {
              this.setIsFetching(false);
            });

          return;
        }

        case 'upvote': {
          const headers = {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$cookies.get('csrftoken'),
          };

          this.$axios.post(
            `/comments/like/${id}/`,
            {},
            { headers },
          )
            .then((response) => {
              const { success, upvote_count: upvoteCount } = (response && response.data) || {};

              done({ type, value: upvoteCount, error: !success });
            })
            .catch(() => {
              done({ type, value, error: true });
            })
            .finally(() => {
              this.setIsFetching(false);
            });

          return;
        }

        default:
          break;
      }

      done({ type, value, error: true });
    },
    async enteringView() {
      // only fetch the results if we have not already "enteredView" through pt/p param URLs
      if (this.enteredView) {
        return;
      }

      this.enteredView = true;
      if (this.isModeratorView) {
        await this.fetchCommentStatuses();
      }

      // let's always load the first page when entering view.
      this.fetchComments(1);
    },
    async fetchMoreComments() {
      // prevent if any other action is fetching API
      if (this.isFetching) {
        return;
      }

      // pass default pageToFetch (1) unless there is not a search or thread filter applied
      const pageToFetch = this.searchOrThreadFilterIsApplied ? 1 : this.nextPageToFetch;

      if (this.isModeratorView) {
        this.fetchCommentStatuses();
      }

      await this.fetchComments(pageToFetch);

      // enable for next manual fetch so user can get all the remaining items.
      this.hasFetchedMoreManually = true;
    },
    async fetchComments(pageToFetch = 1) {
      if (this.isLoadingComments && !this.isFirstTimeRequestingComments) {
        return;
      }

      this.isFirstTimeRequestingComments = false;
      this.setLoading(true);

      // Make current comments disappear if starting over
      if (pageToFetch <= 1) {
        this.comments = [];
        this.hasFetchedMoreManually = false;
        this.allItemsLoaded = false;
      }

      const fetchData = this.getFetchUrlAndParams(pageToFetch);

      try {
        const response = await this.$axios.get(fetchData.url, { params: fetchData.params });

        if (response?.data?.comments) {
          const comments = get(response, ['data', 'comments'], []);
          const commentsCount = this.searchTerm || this.filterQuery
            ? comments.length
            : this.commentCount;

          if (this.forReviews) {
            this.$store.dispatch('discussion/setReviewsAmount', commentsCount);
          }

          if (this.forQuestions) {
            this.$store.dispatch('discussion/setQuestionsAmount', commentsCount);
          }

          if (pageToFetch > 0) {
            this.comments = this.comments.concat(comments);

            // it is vital that this is set off of the comments parsed from the API
            this.lastFetchCount = comments.length;
            this.nextPageToFetch = pageToFetch + 1;
          } else {
            this.comments = comments;
          }

          this.setLoading(false);

          // Workaround set the enteredView again right after fetch. For some reason
          // the component data is restared to the initial state right after calling $axios
          // and even more weird is not being caught when using watcher
          // TODO: More debugging to know why this happens
          this.enteredView = true;
        }
      } catch (e) {
        this.setLoading(false);
        this.$toastr.e('Failed to load comments');
      }
    },

    getFetchUrlAndParams(pageToFetch) {
      // Break the cache for moderators so everything is always up to date
      const breakCache = (this.userIsModerator) ? Date.now() : '';

      const params = {
        cache: breakCache,
        for_nuxt_pdp: 'True',
      };

      if (this.searchTerm) {
        params.query = this.searchTerm;
        params.comment_type = this.commentType;
        params.deal_id = this.deal.id;

        return { url: '/api/v2/deals/search_comments/', params };
      }

      if (this.hasFetchedMoreManually) {
        // skip previous page, not next one
        params.skip_pages = pageToFetch - 1;
        this.allItemsLoaded = true;
      } else {
        params.page = pageToFetch;
      }

      params.thread_id = this.commentLink?.id || '';
      params.group = this.commentType;

      return {
        url: `/discussions/api/listcomments/${this.deal.id}/?${this.filterQuery + this.sortQuery}`,
        params,
      };
    },
    async fetchCommentStatuses() { // Want to fetch these at the same time
      const responses = await Promise.all([
        catchify(this.$axios.get(`/discussions/api/unresolved/${this.deal.id}/`)),
        catchify(this.$axios.get(`/discussions/api/unapproved/${this.deal.id}/`)),
      ]);

      const [[, unresolvedResp], [, unapprovedResp]] = responses;

      if (unresolvedResp?.data) {
        const {
          unresolved_review_count: unresolvedReviews,
          unresolved_question_count: unresolvedQuestions,
        } = unresolvedResp?.data;

        this.$store.dispatch('discussion/setUnresolvedReviews', unresolvedReviews);
        this.$store.dispatch('discussion/setUnresolvedQuestions', unresolvedQuestions);
      }

      if (unapprovedResp?.data) {
        const {
          unapproved_review_count: unapprovedReviews,
          unapproved_question_count: unapprovedQuestions,
        } = unapprovedResp?.data;

        this.$store.dispatch('discussion/setUnapprovedReviews', unapprovedReviews);
        this.$store.dispatch('discussion/setUnapprovedQuestions', unapprovedQuestions);
      }
    },
    setLoading(status) {
      this.isLoadingComments = status;
      this.setIsFetching(status);
    },
    setIsFetching(status) {
      this.$store.dispatch('discussion/setIsFetching', status);
    },
    async onClickRefresh(data) {
      const { resetHash = false } = data;

      if (resetHash) {
        this.commentLink = {};
      }

      this.$fetch();
    },
    onChangeFilter({ type, value }) {
      if (type === 'filter') {
        this.filterQuery = filterDict[value];
      } else if (type === 'sort') {
        this.sortQuery = filterDict[value];
      }

      this.fetchComments();
    },
    updateScrollToTopVisibility() {
      this.$store.dispatch(
        'scrollToTop/updateVisibility',
        { hideScrollToTop: this.showCreateItemModal || this.showCreateReplyModal },
      );
    },
    // this is pure insanity, but we need to have different throttles for reviews vs questions
    // ~ otherwise the request for questions that is rendered first on the page causes a throttle
    // ~ for loading questions.
    throttle() {
      if (this.forReviews) {
        this.throttleReviews();
      } else {
        this.throttleQuestions();
      }
    },
    /* eslint-disable func-names */
    throttleQuestions: throttle(function () { this.fetchComments(); }, 30000), // 30 sec throttle
    throttleReviews: throttle(function () { this.fetchComments(); }, 30000), // 30 sec throttle
    /* eslint-enable func-names */
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../styles/utils/breakpoints';

  /* stylelint-disable selector-no-qualifying-type */
  .discussion-container {
    background-color: #fff;

    span > a {
      text-decoration: none;
      font-weight: 600;
    }

    .btn.show-more-comments {
      border-color: #ffbc00;
      background-color: #ffbc00;
      color: #272727;
      min-width: 100px;
      padding: 0.4rem 1.2rem;
      font-size: 12px;
      transition: all 200ms ease-in-out;

      &:hover {
        border-color: #ffcb33;
        background-color: #ffcb33;
      }
    }
  }

  .sign-in-banner {
    padding: 0.75rem 1.25rem;
    margin-bottom: 1rem;
    border-radius: 4px;
    background-color: #f5f5f9;
    text-align: center;
    font-size: 16px;
  }

  @include media-breakpoint-up(lg) {
    .title:not([class*='display-']) {
      font-weight: 700 !important;
    }
  }
  /* stylelint-enable selector-no-qualifying-type */
</style>
